import { ActiveListItem, Vendor, VendorDelta } from '../types/types';
import { MAX_ACCUMULATED_VENDORS } from './appConstants';
import { startOfDay } from 'date-fns';

// Function to sort vendors by date and time
export const sortVendorsByDateAndTime = (vendorsList: Vendor[]): Vendor[] => {
  return [...vendorsList].sort((a, b) => {
    // First, get the earliest date for each vendor
    const aStartDate = a.start_date ? new Date(a.start_date) : new Date();
    const bStartDate = b.start_date ? new Date(b.start_date) : new Date();

    // Compare dates first
    if (aStartDate.getTime() !== bStartDate.getTime()) {
      return aStartDate.getTime() - bStartDate.getTime();
    }

    // If dates are the same, compare opening times
    if (a.localized_schedule?.start_time && b.localized_schedule?.start_time) {
      // Normalize time formats by removing colons and padding to 4 digits
      const aTime = a.localized_schedule.start_time.replace(':', '').padStart(4, '0');
      const bTime = b.localized_schedule.start_time.replace(':', '').padStart(4, '0');

      // Compare as integers (e.g., "0930" vs "1015")
      return parseInt(aTime) - parseInt(bTime);
    }

    // If no time info, maintain original order
    return 0;
  });
};

/**
 * Filter vendors by date.
 *
 * @param vendors - Array of vendors (potentially expanded with displayDate)
 * @param targetDate - The date to filter for
 * @returns Vendors that should appear on the target date
 */
export const filterVendorsByDate = (vendors: Vendor[], targetDate: Date): Vendor[] => {
  const normalizedTargetDate = startOfDay(targetDate);
  const targetDateStr = normalizedTargetDate.toISOString().split('T')[0];

  return vendors.filter(vendor => {
    // If vendor has displayDate (from backend expansion), use it
    // This is the authoritative source for when this vendor instance should appear
    if (vendor.displayDate) {
      const vendorDisplayDate = startOfDay(vendor.displayDate);
      const vendorDisplayDateStr = vendorDisplayDate.toISOString().split('T')[0];
      return vendorDisplayDateStr === targetDateStr;
    }

    // Fallback to original logic for non-expanded vendors
    // (This path should rarely be used if we're using expanded endpoint)
    if (!vendor.start_date) {
      return false;
    }

    const vendorStartDate = startOfDay(new Date(vendor.start_date + 'T12:00:00'));
    const vendorEndDate = vendor.expiry
      ? startOfDay(new Date(vendor.expiry + 'T12:00:00'))
      : vendorStartDate;

    const isInDateRange =
      normalizedTargetDate >= vendorStartDate && normalizedTargetDate <= vendorEndDate;

    if (isInDateRange && vendor.schedule?.days && vendor.schedule.days.length > 0) {
      const jsDay = normalizedTargetDate.getDay(); // 0=Sunday, 1=Monday, ..., 6=Saturday
      const backendDay = jsDay === 0 ? 7 : jsDay; // Convert to 1=Monday, ..., 7=Sunday
      const dayMatches = vendor.schedule.days.includes(backendDay);

      return dayMatches;
    }

    return isInDateRange;
  });
};

/**
 * Returns true if the event's run has definitively ended for the current viewer.
 *
 * Staleness rule: now > combinedDateTime(expiry ?? start_date, end_time ?? start_time+4h)
 * If neither date is available we cannot determine staleness, so we return false.
 */
export function isEventOutdated(vendor: {
  expiry: string | null;
  start_date: string | null;
  schedule: { repeat?: string | null; start_time?: string; end_time?: string } | null | undefined;
}): boolean {
  // Recurring events: only consider the series expired once its expiry date passes.
  // Individual occurrence dates do not mark the event outdated.
  if (vendor.schedule?.repeat && vendor.schedule.repeat !== 'once') {
    if (!vendor.expiry) return false;
    const expiryDate = new Date(`${vendor.expiry}T23:59:59`);
    return new Date() > expiryDate;
  }

  const expiryDay = vendor.expiry ?? vendor.start_date;
  if (!expiryDay) return false;
  if (!vendor.schedule) return false;

  let endHour: number,
    endMin: number,
    endDayOffset = 0;
  const endTimeStr = vendor.schedule.end_time;
  const startTimeStr = vendor.schedule.start_time;

  if (endTimeStr) {
    [endHour, endMin] = endTimeStr.split(':').map(Number);
    if (startTimeStr) {
      const [sh, sm] = startTimeStr.split(':').map(Number);
      // end_time earlier than start_time means the event crosses midnight
      // (e.g. 22:00 → 02:00) → the end falls on the next calendar day.
      if (endHour * 60 + endMin < sh * 60 + sm) endDayOffset = 1;
    }
  } else if (startTimeStr) {
    const [sh, sm] = startTimeStr.split(':').map(Number);
    const total = sh * 60 + sm + 240; // +4 hours
    endHour = Math.floor(total / 60) % 24;
    endMin = total % 60;
    if (total >= 1440) endDayOffset = 1; // wraps past midnight → end is next calendar day
  } else {
    endHour = 23;
    endMin = 59;
  }

  const baseDate = new Date(`${expiryDay}T00:00:00`);
  baseDate.setDate(baseDate.getDate() + endDayOffset);
  // Format in local time (not toISOString, which converts to UTC and can
  // shift the date by a day in timezones ahead of UTC).
  const endDateStr = `${baseDate.getFullYear()}-${String(baseDate.getMonth() + 1).padStart(2, '0')}-${String(baseDate.getDate()).padStart(2, '0')}`;

  const endDateTime = new Date(
    `${endDateStr}T${String(endHour).padStart(2, '0')}:${String(endMin).padStart(2, '0')}:00`
  );
  return new Date() > endDateTime;
}

/** Thin adapter so `ActiveListItem` (which carries vendor date fields) can use the same check. */
export function isActiveListItemOutdated(item: ActiveListItem): boolean {
  return isEventOutdated({
    expiry: item.vendor_expiry,
    start_date: item.event_date,
    schedule: item.vendor_schedule ?? {
      start_time: item.vendor_schedule_start_time,
      end_time: item.vendor_schedule_end_time,
    },
  });
}

export const deduplicateVendors = (vendors: Vendor[]): Vendor[] => {
  const vendorMap: Record<number, Vendor> = {};
  vendors.forEach(vendor => {
    if (!vendorMap[vendor.id]) {
      vendorMap[vendor.id] = vendor;
    }
  });
  return Object.values(vendorMap);
};

/**
 * Merges a VendorDelta into the accumulated vendor map.
 * - Upserts added + updated by displayID
 * - Removes entries whose displayID starts with "{removedId}-"
 * - Evicts oldest entries (by displayDate) if over MAX_ACCUMULATED_VENDORS
 */
export function mergeVendorUpdates(
  accumulated: Map<string, Vendor>,
  delta: VendorDelta
): Map<string, Vendor> {
  const next = new Map(accumulated);

  for (const v of [...delta.added, ...delta.updated]) {
    if (v.displayID) next.set(v.displayID, v);
  }

  for (const removedId of delta.removed) {
    const prefix = removedId.toString() + '-';
    for (const key of next.keys()) {
      if (key.startsWith(prefix)) next.delete(key);
    }
  }

  if (next.size > MAX_ACCUMULATED_VENDORS) {
    const sorted = Array.from(next.entries()).sort((a, b) => {
      const dateA = a[1].displayDate?.getTime() ?? 0;
      const dateB = b[1].displayDate?.getTime() ?? 0;
      return dateA - dateB;
    });
    const toEvict = sorted.slice(0, next.size - MAX_ACCUMULATED_VENDORS);
    for (const [key] of toEvict) next.delete(key);
  }

  return next;
}
